<template>
  <div class="settings-container step-four">
    <div v-if="isEdit" class="subtitle">ad start date {{adStartDate}}</div>
    <div v-if="isEdit && hasStarted" class="date-picker">
      <label>edit ad end date below</label>
      <VueCtkDateTimePicker
          v-model="selectedDate"
          id="datepicker"
          no-label
          color="#128ff9"
          no-value-to-custom-elem
          no-button
          no-shortcuts
          only-date
          inline
          :min-date="today"
          no-keyboard
          format="YYYY-MM-DD HH:MM:SS"
          @input="setEndDate">
      </VueCtkDateTimePicker>
    </div>
    <date-range-picker
        v-else
        :timeframe="timeframe"
        :options="{
          changeAction: 'setTimeframe',
          class: 'inline',
          format: 'YYYY-MM-DD HH:MM:SS',
          inline: true,
          label: 'date range during which the image will be available for retailers to send',
          minDate: today,
          noKeyboard: true,
          noShortcuts: true
        }"
        @updateTimeframe="setTimeframe"></date-range-picker>

    <toggle-section-slide
      key="send_time_range"
      toggleText="send time range"
      wrapperClasses="time-range-picker-wrapper"
      :shouldOpen="showTimePicker"
      @onToggleTextClick="showTimePicker = !showTimePicker">
      <div class="time-picker-wrapper" slot="mainContent">
        <em class="time-range-top-label">the ad will be available to send in between</em>
        <div class="picker-wrapper">
          <div :class="['time-range-display', { 'has-error': hasTimeRangeError }]" @click="togglePicker">{{ formattedTimeRange }}</div>
          <sb-time-range-picker
            :class="isShowingTimePicker ? 'open' : 'closed'"
            :interval="{
              start: new Date(2000, 0, 1, 8),
              end: new Date(2000, 0, 1, 21)
            }"
            :start.sync="timeRangeStart"
            :end.sync="timeRangeEnd"
            ></sb-time-range-picker>
        </div>
        <em class="time-range-bottom-label">using retailer timezone</em>
      </div>
    </toggle-section-slide>

    <div :class="['tracking-wrapper', { disabled: adSent }]">
      <label>track a customers behavior for how many hours after they receive your ad</label>
      <input
          :disabled="adSent"
          id="tracking_hours"
          name="tracking_hours"
          type="number"
          min="0"
          max="720"
          step="1"
          v-model="trackingHours"
          @change="validateTrackingHours()">
    </div>
  </div>

</template>

<script>
  import DateRangePicker from '../Shared/DateRangePicker.vue'
  import Modal from '../Shared/Modal'
  import moment from 'moment'
  import ToggleSectionSlide from '../Shared/ToggleSectionSlide'
  import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
  import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
  import { format } from "date-fns"
  import { removeTimeZoneAndGetDate } from "../../utilities"

  export default {
    props: {
      ad: Object,
      isEdit: Boolean,
      adSent: Boolean
    },

    name: 'StepFour',

    components: {
      DateRangePicker,
      Modal,
      ToggleSectionSlide,
      VueCtkDateTimePicker,
    },

    data() {
      return {
        isShowingTimePicker: false,
        hasTimeRangeError: false,
        selectedDate: null,
        showTimePicker: false,
        timeframe: null,
        timeRangeEnd: null,
        timeRangeStart: null,
        today: moment().format('YYYY/MM/DD 00:00:00'),
        trackingHours: null,
      }
    },

    methods: {
      togglePicker() {
        this.isShowingTimePicker = !this.isShowingTimePicker
      },

      setData(field, value) {
        const dataToUpdate = { [field]: value }
        this.$emit('updateAd', dataToUpdate)
      },

      setEndDate() {
        const end = moment(this.selectedDate).format('YYYY/MM/DD 23:59:59'),
            datesData = { send_at_end: end }
        this.$emit('updateAd', datesData)
      },

      setErrors(errorKey, message) {
        this.$emit('setPageErrors', errorKey, message)
      },

      setTimeframe(datesData) {
        const hasStartDate = !!datesData?.send_at_start,
            hasEndDate = !!datesData?.send_at_end,
            rangeSelected = hasStartDate && hasEndDate
        this.timeframe = { start: datesData?.send_at_start, end: datesData?.send_at_end }
        if (rangeSelected) { this.validateTimeframe(true) }
      },

      setTimeRange() {
        this.timeRangeStart = removeTimeZoneAndGetDate(this.ad.time_range_start)
        this.timeRangeEnd = removeTimeZoneAndGetDate(this.ad.time_range_end)
      },

      setValues() {
        const adHasTimeRange = !!this.ad.time_range_start && !!this.ad.time_range_end
        this.showTimePicker = adHasTimeRange
        this.isShowingTimePicker = adHasTimeRange
        this.trackingHours = this.ad.tracking_hours
        this.timeframe = this.ad.send_at_start ? { start: moment.utc(this.ad.send_at_start), end: moment.utc(this.ad.send_at_end) } : null
        this.selectedDate = this.adEndDate
      },

      updateAd(dataToUpdate, additionalParams, isForCompletion) {
        this.$emit('updateAd', dataToUpdate, additionalParams, isForCompletion)
      },

      // FIXME: replace the validation here with a validation library later
      updateAdWithSendTimeRange() {
        const duration = this.timeRangeEnd?.getHours() - this.timeRangeStart?.getHours(),
              dataToUpdate = {
                time_range_start: this.timeRangeStart ? format(this.timeRangeStart, 'H:mm:ss') : this.timeRangeStart,
                time_range_end: this.timeRangeEnd ? format(this.timeRangeEnd, 'H:mm:ss') : this.timeRangeEnd
              }
        this.hasTimeRangeError = !((!this.timeRangeStart && !this.timeRangeEnd) || (duration > 0))
        const errorMessage = this.hasTimeRangeError ? 'please select at least 1 hour' : ''
        this.$emit('setPageErrors', 'timeRange', errorMessage)
        !this.hasTimeRangeError && this.$emit('updateAd', dataToUpdate)
      },

      validateTimeframe(shouldUpdate) {
        const isValid = !!this.timeframe?.start && !!this.timeframe?.end,
            errorMessage = isValid ? '' : 'must select a date range',
            dataToUpdate = { send_at_start: this.timeframe?.start, send_at_end: this.timeframe?.end }
        this.$emit('setPageErrors', 'timeframe', errorMessage)
        if (isValid && shouldUpdate) { this.$emit('updateAd', dataToUpdate) }
      },

      validateTrackingHours() {
        this.trackingHours ? this.trackingHours = this.trackingHours.toString().replace(/\D/g, '') : null
        const isValid = this.trackingHours && this.trackingHours >= 1 && this.trackingHours <= 720,
            errorMessage = isValid ? '' : 'tracking hours must be between 1-720 hours'

        this.$emit('setPageErrors', 'trackingHours', errorMessage)
        if (isValid) { this.setData('tracking_hours', this.trackingHours) }
      },
    },

    created() {
      if (this.ad) { this.setValues(); this.setTimeRange() }
    },

    watch: {
      ad: function() {
        this.setValues()
      },
      timeRangeStart: function() {
        this.updateAdWithSendTimeRange()
      },
      timeRangeEnd: function() {
        this.updateAdWithSendTimeRange()
      }
    },

    computed: {
      adStartDate: function() {
        return moment.utc(this.ad.send_at_start).format('MMM DD, YYYY')
      },

      adEndDate: function() {
        return moment.utc(this.ad.send_at_end)
      },

      hasStarted: function () {
        const now = moment().unix()
        return now > moment(this.ad.send_at_start).unix()
      },

      formattedTimeRange() {
        const hasTimeRange = this.timeRangeStart && this.timeRangeEnd,
              formattedStartTime = this.timeRangeStart ? format(this.timeRangeStart, 'ha') : this.timeRangeStart,
              formattedEndTime = this.timeRangeEnd ? format(this.timeRangeEnd, 'ha') : this.timeRangeEnd,
              ifHasTimeRange = `${formattedStartTime} - ${formattedEndTime}`,
              ifHasOnlyStartTime = formattedStartTime ? `${formattedStartTime}` : "-"
        return hasTimeRange ? ifHasTimeRange : ifHasOnlyStartTime
      }
    },

  }
</script>

<style lang="sass">
@import '../../assets/sass/colors'

.step-four
  height: 100%
  margin: 1rem
  position: relative
  text-align: center

  .date-picker
    display: flex
    flex-direction: column
    justify-content: center
    margin: 2rem auto
    max-width: 400px

    label
      margin: 0 auto 1rem auto

    .date-time-picker
      width: auto

    #datepicker-wrapper
      border: 1px solid $primary-color

  .tracking-wrapper
    &.disabled
      opacity: .5

    label
      margin-right: .5rem

    input
      width: 200px
      @media only screen and (max-width: 40em)
        margin-top: .5rem

.time-range-picker-wrapper
  .slide-section
    &__slider
      &.slide-down
        max-height: 300px
      &.slide-up
        max-height: 0

  .time-picker-wrapper
    align-items: center
    display: flex
    flex-direction: column
    padding: 15px 0
    .time-range-top-label
      color: $title-color
    .time-range-bottom-label
      color: $negative-color
    .time-range-display
      border: 1px solid $primary-color
      border-radius: 10px
      cursor: pointer
      padding: 10px 0
      width: 100%
      &.has-error
        border-color: $negative-hover
    .picker-wrapper
      align-items: center
      display: flex
      flex-direction: column
      margin: 8px auto 5px
      width: 300px
      .error-message
        margin-bottom: 5px
    .time-picker
      &.closed
        max-height: 0
        overflow-y: hidden
        visibility: hidden

.hour-wrapper
  transition: all .5s ease
  &.open
    max-height: 300px
    visibility: visible
  &.closed
    max-height: 0
    overflow-y: hidden
    visibility: hidden
</style>

